import type { DailyAlertNotification } from '../types/daily-alert';
import type { DailyBudgetEvaluation, DailyUserBudget } from './daily-user-budget';

const HISTORY_DAYS = 7;

export type DailyAlertDismissals = Record<string, string>;

const alertContext = (budget: DailyUserBudget): string => {
  switch (budget.costQuality) {
    case 'authoritative':
      return "Today's authoritative cost (UTC)";
    case 'estimated':
      return "Today's estimated allocation (UTC)";
    case 'calculated':
      return "Today's calculated cost (UTC)";
  }
};

const subtractUtcDays = (dateKey: string, days: number): string => {
  const date = new Date(`${dateKey}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
};

export const parseStoredDailyNotifications = (value: string | null): DailyAlertNotification[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(notification =>
      notification
      && typeof notification.id === 'string'
      && typeof notification.apiKeyName === 'string'
      && Number.isFinite(notification.cost)
      && typeof notification.date === 'string'
      && typeof notification.dismissed === 'boolean'
    );
  } catch {
    return [];
  }
};

export const parseStoredDailyDismissals = (value: string | null): DailyAlertDismissals => {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const entries = Object.entries(parsed as Record<string, unknown>).filter(
      (entry): entry is [string, string] => {
        const [id, date] = entry;
        return (
        typeof id === 'string' && typeof date === 'string'
        );
      }
    );
    return Object.fromEntries(entries);
  } catch {
    return {};
  }
};

export const applyDailyDismissals = (
  notifications: DailyAlertNotification[],
  dismissals: DailyAlertDismissals
): DailyAlertNotification[] => notifications.map(notification =>
  dismissals[notification.id]
    ? { ...notification, dismissed: true }
    : notification
);

export const pruneDailyDismissals = (
  dismissals: DailyAlertDismissals,
  currentDay: string
): DailyAlertDismissals => {
  const cutoff = subtractUtcDays(currentDay, HISTORY_DAYS);
  return Object.fromEntries(
    Object.entries(dismissals).filter(([, date]) => date >= cutoff)
  );
};

/**
 * Reconcile one complete UTC-day evaluation with persisted notifications.
 * Partial/error results never reach this function, so an empty evaluation can
 * safely remove active alerts while retaining dismissed audit history.
 */
export const reconcileDailyNotifications = (
  previous: DailyAlertNotification[],
  evaluation: DailyBudgetEvaluation
): DailyAlertNotification[] => {
  const currentDay = evaluation.date;
  const historyCutoff = subtractUtcDays(currentDay, HISTORY_DAYS);
  const latest = evaluation.exceeded.map<DailyAlertNotification>(budget => ({
    id: `daily-user-budget:${budget.id}:${currentDay}`,
    apiKeyName: budget.displayName,
    cost: budget.todayCost,
    date: currentDay,
    costQuality: budget.costQuality,
    alertContext: alertContext(budget),
    sources: budget.sources,
    dismissed: false
  }));
  const latestById = new Map(latest.map(notification => [notification.id, notification]));
  const next: DailyAlertNotification[] = previous.filter(notification =>
    notification.date !== currentDay && notification.date >= historyCutoff
  );

  for (const notification of previous) {
    if (notification.date !== currentDay) continue;
    const replacement = latestById.get(notification.id);

    if (notification.dismissed) {
      next.push(replacement
        ? { ...notification, ...replacement, dismissed: true }
        : notification);
      latestById.delete(notification.id);
      continue;
    }

    if (replacement) {
      next.push({ ...notification, ...replacement, dismissed: false });
      latestById.delete(notification.id);
    }
  }

  next.push(...latestById.values());
  return next;
};
